import { createContext, useContext, useCallback, useMemo } from 'react';
import { useUser } from '@auth0/nextjs-auth0';
import useSWR from 'swr';
import axios from 'axios';
import Toast from '@components/toast';
import { isProductionDomain } from '@lib/utils';

// ---------------------------------------------------------

const UserContext = createContext(null);

const fetcher = (url) => axios.get(url).then((res) => res.data);

// ---------------------------------------------------------

// eslint-disable-next-line react/prop-types
export const UserProvider = ({ children }) => {
  const { user: auth0User, isLoading: auth0Loading } = useUser();
  const isAuthenticated = !!auth0User && !auth0Loading;

  // Fetch enriched user info (agent association, etc.) — only when authenticated
  const { data: accountData, mutate: mutateAccount } = useSWR(
    isAuthenticated ? '/api/user/account' : null,
    fetcher
  );

  // Fetch saved listings — only when authenticated
  const {
    data: savedData,
    isLoading: savedLoading,
    error: savedError,
    mutate: mutateSaved
  } = useSWR(isAuthenticated ? '/api/user/saved' : null, fetcher);

  const userInfo = useMemo(() => {
    if (!auth0User) return {};
    return { ...auth0User, agent: accountData?.associatedAgent ?? null };
  }, [auth0User, accountData]);

  const saved = useMemo(() => {
    const listings = savedData?.saved || [];
    return listings.sort((a, b) => b.lastSavedOn - a.lastSavedOn);
  }, [savedData]);

  const updateSavedListing = useCallback(
    async (opts) => {
      try {
        const { data } = await axios.put('/api/user/saved', opts);
        const savedListing = { ...opts, ...data };
        const shouldRemove =
          !savedListing.isFavorited &&
          !savedListing.shouldNotifyOnOpenHouseScheduled &&
          !savedListing.shouldNotifyOnPriceChange &&
          !savedListing.shouldNotifyOnStatusChange &&
          savedListing.noteCount == 0;

        // Optimistic update; merge keeps hydrated fields the PUT response lacks
        mutateSaved(
          (current) => {
            const listings = current?.saved || [];
            const index = listings.findIndex(
              (el) => el.savedListingId === savedListing.savedListingId
            );
            const next = [...listings];
            if (index !== -1 && shouldRemove) {
              next.splice(index, 1);
            } else if (index !== -1) {
              next[index] = { ...listings[index], ...savedListing };
            } else {
              next.unshift(savedListing);
            }
            return { saved: next };
          },
          { revalidate: false }
        );

        Toast.notify({
          message: `${!shouldRemove ? 'S' : 'Removed s'}aved listing successfully`,
          type: 'success'
        });

        // Revalidate: the PUT response is skinny and nothing else refreshes this cache
        mutateSaved();
        return true;
      } catch (ex) {
        if (!isProductionDomain()) console.error(ex);
        // Revalidate to get server truth on error
        mutateSaved();
        Toast.notify({
          message: 'There was a problem updating this saved listing.',
          type: 'danger'
        });
        return false;
      }
    },
    [mutateSaved]
  );

  const updateAssociatedAgent = useCallback(
    async (agentId) => {
      try {
        await axios.put('/api/user/account', { associatedAgent: agentId });
        mutateAccount();
      } catch (ex) {
        if (!isProductionDomain()) console.error(ex);
      }
    },
    [mutateAccount]
  );

  const value = useMemo(
    () => ({
      // Auth
      isAuthenticated,
      isLoading: auth0Loading,
      // User info
      userInfo,
      // Saved listings
      saved,
      savedLoading,
      savedError,
      // Mutations
      updateSavedListing,
      updateAssociatedAgent,
      mutateSaved
    }),
    [
      isAuthenticated,
      auth0Loading,
      userInfo,
      saved,
      savedLoading,
      savedError,
      updateSavedListing,
      updateAssociatedAgent,
      mutateSaved
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// ---------------------------------------------------------
// Hooks — replace Redux selectors
// ---------------------------------------------------------

const useUserContext = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUserContext must be used within UserProvider');
  return ctx;
};

export const useAuthenticated = () => {
  const { isAuthenticated, isLoading } = useUserContext();
  return { isAuthenticated, isLoading };
};

export const useUserInfo = () => {
  const { userInfo, isLoading } = useUserContext();
  return { userInfo, isLoading };
};

export const useSaved = () => {
  const { saved, savedLoading, savedError, mutateSaved } = useUserContext();
  return { saved, isLoading: savedLoading, error: savedError, mutateSaved };
};

export const useSavedListing = (listingId) => {
  const { saved } = useUserContext();
  const savedListing = saved.find(({ listingId: id }) => id === listingId) || null;
  const isFavorited = savedListing?.isFavorited || false;
  return { savedListing, isFavorited };
};

export const useUpdateSavedListing = () => {
  const { updateSavedListing } = useUserContext();
  return updateSavedListing;
};

export const useUpdateAssociatedAgent = () => {
  const { updateAssociatedAgent } = useUserContext();
  return updateAssociatedAgent;
};
